import {
    Avatar,
    Colors,
    Text,
    getRelativeTime,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { memo } from 'react';

import type { TextAnswer } from '@/components/QuestionResultContainer/types';

import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';
import i18n from '@/plugins/i18n';
import { getInitials, getProfilePictureUrl } from '@/utils/user';

export interface CommentAnswerCardProps {
    answer: TextAnswer;
    userAnswer?: string;
    isReply?: boolean;
    className?: string;
    dataTestId?: string;
}

export const CommentAnswerCard = memo(
    ({
        answer,
        userAnswer,
        isReply = false,
        className = '',
        dataTestId = 'comment-answer-card',
    }: CommentAnswerCardProps) => {
        const { t } = useAmelioTranslations();

        const reviewerName = answer.user
            ? `${answer.user.firstName} ${answer.user.lastName}`
            : t('questionResult.anonymous');

        const reviewerInitials = answer.user ? getInitials(answer.user) : 'A';
        const reviewerAvatar = getProfilePictureUrl(answer.user?.profilePictureUrl ?? '');

        const timestamp = getRelativeTime({
            date: answer.submittedAt,
            language: i18n.language,
        });

        return (
            <div className={`flex items-start gap-3 ${className}`}>
                <TooltipProvider>
                    <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                            <div
                                className={'shrink-0 cursor-default'}
                                data-testid={`${dataTestId}-avatar-trigger`}
                            >
                                <Avatar
                                    alt={reviewerName}
                                    initials={reviewerInitials}
                                    src={reviewerAvatar}
                                    size={'xs'}
                                    dataTestId={`${dataTestId}-avatar`}
                                />
                            </div>
                        </TooltipTrigger>

                        <TooltipContent side={'top'}>
                            <p>{reviewerName}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>

                <div
                    className={`border-base-100 flex min-w-0 flex-1 flex-col rounded-lg border bg-white p-2`}
                    data-testid={dataTestId}
                >
                    <div className={'items-start justify-between sm:flex'}>
                        <span
                            className={'min-w-0 flex-1'}
                            data-testid={`${dataTestId}-reviewer-name`}
                        >
                            <Text
                                color={Colors.BASE}
                                weight={'semibold'}
                                className={'truncate leading-tight'}
                            >
                                {reviewerName}
                            </Text>
                        </span>

                        <span
                            className={'shrink-0'}
                            data-testid={`${dataTestId}-date`}
                        >
                            <Text
                                color={Colors.BASE_400}
                                size={'small'}
                            >
                                {timestamp}
                            </Text>
                        </span>
                    </div>

                    {userAnswer && !isReply && (
                        <Text
                            color={Colors.BASE_600}
                            size={'small'}
                            className={'mt-1 wrap-break-word'}
                        >
                            {`${t('questionResult.userAnswer')} ${userAnswer}`}
                        </Text>
                    )}

                    <span
                        data-testid={`${dataTestId}-text`}
                        className={'block'}
                    >
                        <Text
                            color={Colors.BASE}
                            className={'mt-2 leading-relaxed break-words whitespace-pre-wrap'}
                        >
                            {answer.text}
                        </Text>
                    </span>
                </div>
            </div>
        );
    },
);

CommentAnswerCard.displayName = 'CommentAnswerCard';
